/**
 * Liberação paramétrica de lotes.
 *   - Lista batches em status PREPARING / IN_PROGRESS.
 *   - Painel de detalhes: parâmetros observados + BI + ação aprovar/rejeitar.
 *   - Aprovado: dispara PDF (server-side) e atualiza validade dos kits.
 */
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, XCircle, FlaskConical, FileText, Send } from 'lucide-react';
import { apiGet, apiPost, ApiError } from '@/shared/api/client';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Input } from '@/components/ui/Input';

interface BatchRow {
  id: string;
  batch_code: string;
  status: 'PREPARING' | 'IN_PROGRESS' | 'RELEASED' | 'REJECTED' | 'RECALLED' | string;
  equipment_id: string;
  equipment_name?: string | null;
  equipment_type?: string | null;
  operator_name?: string | null;
  started_at: string;
  finished_at?: string | null;
  temperature_c?: number | null;
  pressure_kpa?: number | null;
  duration_min?: number | null;
  load_weight_kg?: number | null;
  bowie_dick_ok?: number | null;
}

interface BatchDetail extends BatchRow {
  contents: { id: string; item_id: string | null; kit_instance_id: string | null; item_barcode: string | null; kit_barcode: string | null; kit_alias: string | null }[];
  tests: { id: string; kind: string; result: string; lot_number: string | null; performed_at: string }[];
}

const STATUS_TONE: Record<string, 'success' | 'warning' | 'danger' | 'info' | 'neutral'> = {
  RELEASED: 'success',
  REJECTED: 'danger',
  RECALLED: 'danger',
  PREPARING: 'warning',
  IN_PROGRESS: 'info',
};

export function LiberacaoPage() {
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ['batches', 'pending-release'],
    queryFn: async () => {
      const prep = await apiGet<{ items: BatchRow[] }>('/v1/batches?status=PREPARING');
      const prog = await apiGet<{ items: BatchRow[] }>('/v1/batches?status=IN_PROGRESS');
      return { items: [...prep.items, ...prog.items] };
    },
    refetchInterval: 30_000,
  });

  const [picked, setPicked] = useState<string | null>(null);
  const detail = useQuery({
    queryKey: ['batches', 'detail', picked],
    enabled: !!picked,
    queryFn: () => apiGet<BatchDetail>(`/v1/batches/${picked}`),
  });

  // Form state for release
  const [tempC, setTempC] = useState('');
  const [pressureKpa, setPressureKpa] = useState('');
  const [durationMin, setDurationMin] = useState('');
  const [loadKg, setLoadKg] = useState('');
  const [notes, setNotes] = useState('');
  const [biLot, setBiLot] = useState('');
  const [biResult, setBiResult] = useState<'NEGATIVE' | 'POSITIVE' | 'INVALID'>('NEGATIVE');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!detail.data) return;
    setTempC(detail.data.temperature_c?.toString() ?? '');
    setPressureKpa(detail.data.pressure_kpa?.toString() ?? '');
    setDurationMin(detail.data.duration_min?.toString() ?? '');
    setLoadKg(detail.data.load_weight_kg?.toString() ?? '');
    setNotes('');
    setError(null);
  }, [detail.data?.id]);

  const release = useMutation({
    mutationFn: (approved: boolean) => apiPost<{ id: string; status: string }>(
      `/v1/batches/${picked}/release`,
      {
        approved,
        temperature_c: Number(tempC),
        pressure_kpa: pressureKpa ? Number(pressureKpa) : undefined,
        duration_min: Number(durationMin),
        load_weight_kg: Number(loadKg),
        notes: notes || undefined,
      },
      { idempotencyKey: `release-${picked}-${approved ? 'ok' : 'no'}` },
    ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['batches'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      setPicked(null);
    },
    onError: (e) => {
      if (e instanceof ApiError) setError(`${e.code}: ${e.message}`);
      else setError((e as Error).message);
    },
  });

  const addBi = useMutation({
    mutationFn: () => apiPost(
      `/v1/batches/${picked}/biological-indicator`,
      { lot_number: biLot || undefined, incubation_minutes: 60 },
      { idempotencyKey: `bi-${picked}-${Date.now()}` },
    ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['batches', 'detail', picked] });
    },
  });

  const addBiReading = useMutation({
    mutationFn: async () => {
      const d = await apiGet<{ items: { id: string; kind: string; result: string }[] }>(`/v1/batches/${picked}`).then((b: any) => ({ items: b.tests }));
      const bi = d.items.find((t: any) => t.kind === 'BIOLOGICAL' || t.kind === 'BI');
      if (!bi) throw new Error('Nenhum BI registrado ainda');
      return apiPost(`/v1/batches/biological-indicator/${bi.id}/read`, { final_result: biResult });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['batches', 'detail', picked] }),
  });

  const validForm = useMemo(() => {
    return tempC && Number(tempC) > 0 && durationMin && Number(durationMin) > 0 && loadKg && Number(loadKg) >= 0;
  }, [tempC, durationMin, loadKg]);

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight text-neutral-900 dark:text-neutral-50">
          Liberação paramétrica
        </h1>
        <p className="text-sm text-neutral-500">
          Confira parâmetros observados, indicadores biológicos e libere (ou rejeite) o lote.
          Apenas Enfermeiro RT ou Supervisor pode liberar.
        </p>
      </header>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-1">
          <CardHeader title="Lotes aguardando liberação" subtitle={list.isLoading ? 'Carregando…' : `${list.data?.items.length ?? 0} pendentes`} />
          <CardBody className="p-0">
            <ul className="max-h-[28rem] divide-y divide-neutral-200 overflow-y-auto dark:divide-neutral-700">
              {(list.data?.items ?? []).map((b) => (
                <li key={b.id}>
                  <button
                    type="button"
                    onClick={() => setPicked(b.id)}
                    className={`flex w-full items-start justify-between gap-3 px-4 py-3 text-left transition-colors ${picked === b.id ? 'bg-brand-50 dark:bg-brand-900/30' : 'hover:bg-neutral-100 dark:hover:bg-neutral-800'}`}
                  >
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium">{b.batch_code}</p>
                      <p className="font-mono text-xxs text-neutral-500">{b.equipment_name ?? '—'}</p>
                    </div>
                    <Badge tone={STATUS_TONE[b.status] ?? 'neutral'}>{b.status}</Badge>
                  </button>
                </li>
              ))}
              {!list.isLoading && (list.data?.items?.length ?? 0) === 0 ? (
                <li className="p-8 text-center text-xs text-neutral-500">Nenhum lote pendente.</li>
              ) : null}
            </ul>
          </CardBody>
        </Card>

        <div className="space-y-4 lg:col-span-2">
          {!picked ? (
            <Card>
              <CardBody className="grid place-items-center p-10 text-sm text-neutral-500">
                <FlaskConical size={36} strokeWidth={1.5} className="mb-2 text-neutral-400" />
                Selecione um lote para conferir.
              </CardBody>
            </Card>
          ) : !detail.data ? (
            <Card><CardBody className="p-10 text-center text-sm text-neutral-500">Carregando lote…</CardBody></Card>
          ) : (
            <>
              <Card>
                <CardHeader
                  title={`Lote ${detail.data.batch_code}`}
                  subtitle={`${detail.data.equipment_name ?? ''} · iniciado ${detail.data.started_at}`}
                  action={<Badge tone={STATUS_TONE[detail.data.status] ?? 'neutral'}>{detail.data.status}</Badge>}
                />
                <CardBody className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  <Input label="Temperatura observada (°C)" type="number" step="0.1" value={tempC} onChange={(e) => setTempC(e.target.value)} />
                  <Input label="Pressão (kPa)" type="number" step="0.1" value={pressureKpa} onChange={(e) => setPressureKpa(e.target.value)} hint="Opcional (plasma não usa)" />
                  <Input label="Duração (min)" type="number" value={durationMin} onChange={(e) => setDurationMin(e.target.value)} />
                  <Input label="Carga total (kg)" type="number" step="0.01" value={loadKg} onChange={(e) => setLoadKg(e.target.value)} />
                  <div className="md:col-span-2">
                    <Input label="Observações" value={notes} onChange={(e) => setNotes(e.target.value)} />
                  </div>
                </CardBody>
              </Card>

              <Card>
                <CardHeader title="Indicador biológico" subtitle="Iniciar incubação ou registrar leitura final" />
                <CardBody className="space-y-3">
                  <ul className="divide-y divide-neutral-200 dark:divide-neutral-700">
                    {detail.data.tests.length === 0 ? (
                      <li className="py-2 text-xs text-neutral-500">Nenhum BI/QI registrado.</li>
                    ) : detail.data.tests.map((t) => (
                      <li key={t.id} className="flex items-center justify-between py-2 text-sm">
                        <span>{t.kind} · lote {t.lot_number ?? '—'}</span>
                        <Badge tone={t.result === 'APPROVED' || t.result === 'NEGATIVE' ? 'success' : t.result === 'PENDING' ? 'warning' : 'danger'}>{t.result}</Badge>
                      </li>
                    ))}
                  </ul>
                  <div className="flex flex-wrap items-end gap-2">
                    <div className="flex-1 min-w-48">
                      <Input label="Nº lote BI" value={biLot} onChange={(e) => setBiLot(e.target.value)} placeholder="ex.: 3M-2025-04" />
                    </div>
                    <Button variant="secondary" leadingIcon={<FlaskConical size={16} />} loading={addBi.isPending} onClick={() => addBi.mutate()}>
                      Iniciar incubação
                    </Button>
                    <div className="flex items-center gap-1">
                      {(['NEGATIVE','POSITIVE','INVALID'] as const).map((r) => (
                        <button
                          key={r}
                          type="button"
                          onClick={() => setBiResult(r)}
                          className={`rounded border px-3 py-2 text-xs transition-colors ${biResult === r ? (r === 'NEGATIVE' ? 'border-success bg-success-bg text-success' : r === 'POSITIVE' ? 'border-danger bg-danger-bg text-danger' : 'border-warning bg-warning-bg text-neutral-900') : 'border-neutral-300 dark:border-neutral-700'}`}
                        >
                          {r === 'NEGATIVE' ? 'Negativo' : r === 'POSITIVE' ? 'Positivo' : 'Inválido'}
                        </button>
                      ))}
                    </div>
                    <Button variant="secondary" leadingIcon={<Send size={16} />} loading={addBiReading.isPending} onClick={() => addBiReading.mutate()}>
                      Registrar leitura
                    </Button>
                  </div>
                </CardBody>
              </Card>

              <Card>
                <CardHeader title="Conteúdo do lote" subtitle={`${detail.data.contents.length} item(ns)/kit(s)`} />
                <CardBody className="p-0">
                  <ul className="max-h-48 divide-y divide-neutral-200 overflow-y-auto text-sm dark:divide-neutral-700">
                    {detail.data.contents.map((c) => (
                      <li key={c.id} className="flex items-center justify-between gap-3 px-4 py-2">
                        <span className="truncate">{c.kit_alias ?? c.kit_barcode ?? c.item_barcode ?? '—'}</span>
                        <span className="font-mono text-xxs text-neutral-500">{c.kit_instance_id ? 'KIT' : 'ITEM'}</span>
                      </li>
                    ))}
                  </ul>
                </CardBody>
              </Card>

              {error ? <p className="text-sm text-danger">{error}</p> : null}

              <div className="flex flex-wrap items-center justify-end gap-3 border-t border-neutral-200 pt-4 dark:border-neutral-700">
                <Button
                  variant="danger"
                  size="touch"
                  leadingIcon={<XCircle size={18} />}
                  loading={release.isPending && release.variables === false}
                  disabled={!validForm}
                  onClick={() => release.mutate(false)}
                >
                  Rejeitar
                </Button>
                <Button
                  variant="primary"
                  size="touch"
                  leadingIcon={<CheckCircle2 size={18} />}
                  loading={release.isPending && release.variables === true}
                  disabled={!validForm}
                  onClick={() => release.mutate(true)}
                >
                  Liberar lote
                </Button>
                <span className="ml-2 inline-flex items-center gap-1 text-xs text-neutral-500"><FileText size={14} /> PDF gerado em segundo plano.</span>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
